import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { DateRangeInterface, DSSetup, Service } from 'webapp-types';
import {
	CompareFunction,
	CompareType,
	DateRangeSelectionId,
	TimeUnit,
	type DateRangeTypeId,
} from 'webapp/enums/enums';
import { copyObject, uniq } from 'webapp/utils/common';

import DateRange from 'webapp/models-custom/date-range';
import MetricLimitationCompare from 'webapp/models-custom/metric-limitation/compare';
import type MetricLimitationRange from 'webapp/models-custom/metric-limitation/range';
import MetricSetup from 'webapp/models-custom/metric-setup';
import Serviced from 'webapp/models-custom/serviced';

export const CompareCategory = {
	NONE: 0,
	PREVIOUS: 1,
	NEXT: 2,
	CUSTOM: 3,
	GOAL: 4,
	OTHER_METRIC: 5,
};

// the compare's properties, from its formula (getModelPropsFromFormula, updateModelFromProps)
interface DsCompareProps {
	id?: number;
	type?: CompareType;
	typeId?: string;
	selectionId?: string;
	title?: string;
	categoryId?: number; // one of CompareCategory
	typeIdVariations?: string[];
	overrideFormat?: string;
	overrideFunction?: CompareFunction;
	offset?: number;
	timeUnit?: number;
	constValue?: number;
	metricSetup?: DSSetup.MetricSetupData;
	dateRange?: { range: DateRangeInterface.RangeInterface };
}

interface MetricDefaults {
	format: string;
	changeFormat: string;
	changeFunction: CompareFunction;
}

export default class DsCompare extends Serviced {
	static customNoneId = 'None';

	static defaultFunction = CompareFunction.PERCENTAGE;
	static defaultFormat = '+0%'; // same as constantsService.defaultChangeFormat

	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked id: number | null = null; // just keeping for BE

	@tracked constValue: number | null = null; // only set in special cases (gauge / progress), manual set and removal

	@tracked overrideFunction: CompareFunction | null = null;
	@tracked overrideFormat: string | null = null;

	@tracked title = ''; // only for predefined

	@tracked type: CompareType | null = null; // from formula CompareType enums

	// only used to determine which compare type to use for limitations
	@tracked typeId = DsCompare.customNoneId;

	// used to determine which item is chosen on the predefined list on UI
	// similar to typeId but less specific (selectionId customPreviousOffsetId encompasses customPreviousOffsetDays/Weeks etc)
	@tracked selectionId = DsCompare.customNoneId;

	@tracked categoryId = CompareCategory.NONE; // one of CompareCategory

	@tracked timeUnit: number | null = null;
	@tracked offset = -1;

	@tracked typeIdVariations: string[] = [];

	@tracked limitations: MetricLimitationCompare[] = []; // set from metric-limitations

	@tracked customNoneText: string | null = null; // used to display "No changes" on master compare

	@tracked _defaultFormat = DsCompare.defaultFormat;
	@tracked _defaultFunction = DsCompare.defaultFunction;

	constructor(props?: DsCompareProps, metricDefaults?: MetricDefaults) {
		super();

		if (metricDefaults) {
			this.updateDefaultsFromMetric(metricDefaults);
		}
		if (props) {
			this.updateModelFromProps(props);
		}
	}

	get isNone(): boolean {
		return this.typeId === DsCompare.customNoneId;
	}

	get isPrevious(): boolean {
		return this.typeId === DsCompare.customPreviousId;
	}

	get isPriorOffset(): boolean {
		return [
			DsCompare.customPreviousOffsetDays,
			DsCompare.customPreviousOffsetWeeks,
			DsCompare.customPreviousOffsetMonths,
			DsCompare.customPreviousOffsetQuarters,
			DsCompare.customPreviousOffsetYears,
		].includes(this.typeId);
	}

	get isCustom(): boolean {
		return this.typeId === DsCompare.customCustomId;
	}

	get isOtherMetric(): boolean {
		return this.typeId === DsCompare.customOtherMetricId;
	}

	get isGoal(): boolean {
		return this.typeId === DsCompare.customGoalId;
	}

	@cached
	get titleUX(): string {
		let title = '';
		if (this.isPriorOffset) {
			let unitString =
				DateRange.baseGranulations.find((item) => item.id === (this.timeUnit as TimeUnit))
					?.title || 'unknown';
			unitString = unitString.toLowerCase();
			if (this.positiveOffset !== 1) {
				unitString += 's';
			}
			title = `Same period ${this.positiveOffset} ${unitString} ago`;
		} else if (this.isCustom) {
			title = this.customDateRangeCM.rangeTitleUX;
		} else if (this.isNone && this.customNoneText) {
			title = this.customNoneText;
		} else if (this.isGoal || this.isOtherMetric) {
			const selectedMetric = this.metricSetup.metric;
			if (selectedMetric) {
				if (this.isGoal && selectedMetric.isTypeGoal) {
					title = selectedMetric.name || ''; // "(Goal)" is added by BE
				} else if (this.isOtherMetric && !selectedMetric.isTypeGoal) {
					title = `${this.metricSetup.selectedMetricTitle || ''} (Other metric)`;
				}
			}
		} else {
			title = this.title;
		}
		return title;
	}

	@cached
	get titleShort(): string {
		let title = this.title;
		if (this.isPrevious) {
			title = 'prev';
		} else if (this.isPriorOffset) {
			const unitString =
				DateRange.baseGranulations.find((item) => item.id === (this.timeUnit as TimeUnit))
					?.titleShort || '?';
			title = `${this.positiveOffset}${unitString} ago`;
		} else if (this.isCustom) {
			title = this.customDateRangeCM.rangeTitleShort;
		} else if (this.isOtherMetric || this.isGoal) {
			const metricTitle = this.customCompareMetricName;
			if (metricTitle) {
				title = metricTitle;
			}
		}
		return title;
	}

	@cached
	get titleVs(): string {
		let title = 'vs ';
		if (this.isPrevious) {
			title += 'previous period';
		} else if (this.isPriorOffset) {
			let unitString =
				DateRange.baseGranulations.find((item) => item.id === (this.timeUnit as TimeUnit))
					?.title || 'unknown';
			unitString = unitString.toLowerCase();
			if (this.positiveOffset !== 1) {
				unitString += 's';
			}
			title += `${this.positiveOffset} ${unitString} ago`;
		} else if (this.isCustom) {
			title += this.customDateRangeCM.rangeTitleUX;
		} else if (this.isGoal) {
			if (this.metricSetup?.selectedMetricTitle) {
				title += this.metricSetup?.selectedMetricTitle;
			} else {
				title += 'goal';
			}
		} else if (this.isOtherMetric) {
			title += this.customCompareMetricName;
		} else {
			title += this.title;
		}
		return title;
	}

	@cached
	get titleVsShort(): string {
		return `vs ${this.titleShort}`;
	}

	get hasExtraSettings(): boolean {
		return this.isCustom || this.isPriorOffset;
	}

	get positiveOffset(): number {
		return Math.abs(this.offset);
	}

	set positiveOffset(value: number) {
		let offsetValue = Math.abs(value);
		if (this.categoryId !== CompareCategory.NEXT) {
			// negative by default
			offsetValue = -1 * offsetValue;
		}
		this.offset = offsetValue;
	}

	get isConstant(): boolean {
		return typeof this.constValue === 'number';
	}

	get rangeFormula(): DateRangeInterface.RangeInterface | null {
		return this.getFormula()?.dateRange?.range || null;
	}

	get defaultFormat(): string {
		return this._defaultFormat;
	}

	set defaultFormat(value: string) {
		this._defaultFormat = value;
	}

	get defaultFormatForCurrentFunction() {
		return this.mmMetricsService.getChangeFormatFromChangeFunction(
			this.function,
			this.defaultFormat,
		);
	}

	get defaultFunction(): CompareFunction {
		return this.isGoal ? CompareFunction.RATIO : this._defaultFunction;
	}

	set defaultFunction(value: CompareFunction) {
		this._defaultFunction = value;

		// updates default format from default function
		let defaultFormat = this._defaultFormat;
		if (this.defaultFunction === CompareFunction.ABSOLUTE) {
			defaultFormat = defaultFormat.replace('%', '');
		}
		this.defaultFormat = defaultFormat;
	}

	get format(): string {
		return this.overrideFormat || this.defaultFormatForCurrentFunction;
	}

	set format(value: string) {
		this.overrideFormat = this.defaultFormatForCurrentFunction === value ? null : value;
	}

	get function(): CompareFunction {
		return this.overrideFunction || this.defaultFunction;
	}

	set function(value: CompareFunction) {
		this.overrideFunction = this.defaultFunction === value ? null : value;
		this.checkFormatFromFunction();
	}

	get typeName(): string {
		// for tracking
		switch (this.selectionId) {
			case DsCompare.customNoneId:
				return 'none';
			case DsCompare.customPreviousId:
				return 'previous_period';
			case DsCompare.customPreviousOffsetId:
				return 'same_period';
			case DsCompare.customCustomId:
				return 'custom_date_range';
			case DsCompare.customOtherMetricId:
				return 'metric';
			case DsCompare.customGoalId:
				return 'goal';
			default:
				return '';
		}
	}

	get displayTitle(): string {
		if (this.isNone && this.customNoneText) {
			return this.customNoneText;
		}
		return this.title;
	}

	get customCompareMetricName() {
		return this.metricSetup.selectedMetricTitle;
	}

	// ------- Custom ranges - need whole dateRange obj -------

	protected _customDateRange!: DateRange;

	protected createCustomDateRange(rangeFormula?: DateRangeInterface.RangeInterface | null): void {
		const dateRange = DateRange.createFromRangeUISelectionId(DateRangeSelectionId.CUSTOM);
		if (rangeFormula) {
			dateRange.updateRange(rangeFormula);
		}
		this._customDateRange = dateRange;
	}

	get customDateRangeCM(): DateRange {
		if (!this._customDateRange) {
			this.createCustomDateRange(this.rangeFormula);
		}

		return this._customDateRange;
	}

	// ------- Comparing with another metric - need metricsetting for source / key -------

	protected _metricSetup: MetricSetup | null = null;

	get metricSetup(): MetricSetup {
		if (!this._metricSetup) {
			const metricSetup = new MetricSetup();

			this._metricSetup = metricSetup;
		}

		return this._metricSetup;
	}

	// ------- From limitations, used in selector (dropdowns) -------
	// these props are only used for those compare objs that are created from predefined compares
	// and have limitations set by metric-limitations

	get isDisabled(): boolean {
		const limitations = this.limitations || [];
		return limitations.length > 0 && limitations.filter((item) => !item.disabled).length === 0;
	}

	get isDisabledForPaymentReasons(): boolean {
		return this.isDisabled && !!(this.limitations || []).find((item) => item.isPaidLimitation);
	}

	get infoText(): string {
		const limitations = this.limitations || [];

		let infoText: string[] = [];

		if (this.isDisabledForPaymentReasons) {
			const paidLimitation = limitations.find((item) => item.isPaidLimitation);
			if (paidLimitation) {
				infoText = [paidLimitation.infoText];
			}
		} else {
			// if all texts are the same, just show the text once
			if (
				(this.isDisabled || limitations.length === 1) &&
				uniq(limitations.map((item) => item.infoText)).length === 1
			) {
				infoText.push(limitations[0]!.infoText);
			} else {
				limitations.forEach((limitation) => {
					if (limitation.infoText) {
						let preText = '';
						if (limitations.length > 1) {
							preText = `[${limitation.id}] `;
						}
						infoText.push(`${preText}${limitation.infoText}`);
					}
				});
			}
		}

		return infoText.join('<br>');
	}

	// --------- Utility fns -------

	updateDefaultsFromMetric(metricDefaults?: MetricDefaults) {
		this._defaultFormat = metricDefaults?.changeFormat || DsCompare.defaultFormat;
		this._defaultFunction = metricDefaults?.changeFunction || DsCompare.defaultFunction;
	}

	checkFormatFromFunction(): void {
		const format = this.mmMetricsService.getChangeFormatFromChangeFunction(
			this.function,
			this.format,
		);
		this.format = format;
	}

	setFormatAndFunctionFromDefaults(): void {
		this.overrideFunction = null;
		this.overrideFormat = null;
		this.checkFormatFromFunction();
	}

	createCopy(): DsCompare {
		// getting all props first and only then creating: only setting props once
		// Avoids "already been used previously in the same computation" error

		const props = DsCompare.getModelPropsFromFormula(this.getFormula(), undefined);

		props.typeId = this.typeId;
		props.selectionId = this.selectionId;
		props.title = this.title;
		props.categoryId = this.categoryId;
		props.typeIdVariations = this.typeIdVariations;

		const compare = new DsCompare(props, undefined);

		compare._defaultFormat = this._defaultFormat;
		compare._defaultFunction = this._defaultFunction;
		compare.customNoneText = this.customNoneText;

		return compare;
	}

	updateFromTypeId(typeId: string, dateRange: DateRange): void {
		const timeUnit = DsCompare.typeIdToTimeUnit(typeId), // if we have timeUnit, means it's previousOffset selection
			selectionId = timeUnit ? DsCompare.customPreviousOffsetId : typeId,
			predefinedCompare = DsCompare.predefinedCompares.find(
				(item) => item.selectionId === selectionId,
			);

		if (!predefinedCompare) {
			this.loggerService.logError('[CDR] predefinedCompare not found', {
				selectionId,
			});
			return;
		}

		const copy = predefinedCompare.createCopy();
		if (typeId !== selectionId) {
			copy.typeId = typeId;
			copy.timeUnit = timeUnit;
		}
		copy.updateFromDateRange(dateRange);

		this.updateFromFormula(copy.getFormula(), dateRange);
	}

	get isValid() {
		// only checking the settings from the formula fields
		const formula = this.formula;
		if (!formula) {
			return true; // none - valid
		}
		if (!formula.type) {
			return false;
		}
		switch (formula.type) {
			case CompareType.RANGE:
				return !!formula.dateRange || typeof formula.config?.constValue === 'number';
			case CompareType.GOAL:
			case CompareType.OTHER_METRIC:
				return !!formula.metricSetup?.key;
			default:
				return false; // no other types supported
		}
	}

	@cached
	get formula(): DSSetup.Compare | null {
		if (this.isNone) {
			return null;
		}

		const formula: DSSetup.Compare = {
			type: this.type as number,
		};

		if (this.id) {
			formula.id = this.id;
		}

		if (this.isConstant) {
			formula.config = {
				constValue: this.constValue!,
			};
		} else if (this.isPrevious || this.isPriorOffset) {
			if (this.timeUnit) {
				formula.dateRange = {
					offset: {
						timeUnit: this.timeUnit,
						offset: this.offset,
					},
				};
			}
		} else if (this.isCustom) {
			formula.dateRange = { range: Object.assign({}, this.customDateRangeCM.rangeFormula) };
		} else if (this.isOtherMetric || this.isGoal) {
			formula.metricSetup = this.metricSetup.metricSetupData || undefined;
		}

		const overrides: DSSetup.Compare['overrides'] = {};
		if (this.overrideFormat) {
			overrides.format = this.overrideFormat;
		}
		if (this.overrideFunction) {
			overrides.function = this.overrideFunction;
		}
		if (Object.keys(overrides).length > 0) {
			formula.overrides = overrides;
		}

		return formula;
	}

	getFormula(): DSSetup.Compare | null {
		// use this to not accidently mutate the (cached) this.formula object
		return copyObject(this.formula);
	}

	isEqual(givenFormula: DSSetup.Compare | null): boolean {
		const getStringFromObject = (obj: object | null | undefined) => {
			return JSON.stringify(obj, obj ? Object.keys(obj).sort() : null);
		};

		const formula1: Partial<DSSetup.Compare> = this.getFormula() || {};

		const formula2: Partial<DSSetup.Compare> = givenFormula || {};

		// compare with sorted keys. careful! sort only works with objects one layer deep
		const offset1Sorted = getStringFromObject(formula1.dateRange?.offset),
			offset2Sorted = getStringFromObject(formula2.dateRange?.offset),
			range1Sorted = getStringFromObject(formula1.dateRange?.range),
			range2Sorted = getStringFromObject(formula2.dateRange?.range),
			overrides1Sorted = getStringFromObject(formula1.overrides),
			overrides2Sorted = getStringFromObject(formula2.overrides),
			config1Sorted = getStringFromObject(formula1.config),
			config2Sorted = getStringFromObject(formula2.config),
			dimensions1Sorted = getStringFromObject(formula1.metricSetup?.dimensions || []),
			dimensions2Sorted = getStringFromObject(formula2.metricSetup?.dimensions || []),
			filters1Sorted = getStringFromObject(formula1.metricSetup?.filters),
			filters2Sorted = getStringFromObject(formula2.metricSetup?.filters);

		return (
			formula1.id === formula2.id &&
			formula1.type === formula2.type &&
			formula1.metricSetup?.key === formula2.metricSetup?.key &&
			formula1.metricSetup?.spaceAccessId === formula2.metricSetup?.spaceAccessId &&
			dimensions1Sorted === dimensions2Sorted &&
			offset1Sorted === offset2Sorted &&
			range1Sorted === range2Sorted &&
			overrides1Sorted === overrides2Sorted &&
			config1Sorted === config2Sorted &&
			filters1Sorted === filters2Sorted
		);
	}

	updateFromFormula(formula: DSSetup.Compare | null, dateRange?: DateRange): void {
		// we pass dateRange here so we can check if this is previous period (depends on dateRange.rangeTimeUnit)
		if (!formula && this.isNone) {
			return;
		}

		const props = DsCompare.getModelPropsFromFormula(formula, dateRange);
		this.updateModelFromProps(props);
		// when resetting to "none" (which means deleting on BE), we must remove id
		if (this.isNone) {
			this.id = null;
		}
	}

	updateModelFromProps(props: DsCompareProps): void {
		if (props.id) {
			this.id = props.id;
		}
		if (props.selectionId) {
			this.selectionId = props.selectionId;
		}
		if (props.typeId) {
			this.typeId = props.typeId;
		}
		if (props.title) {
			this.title = props.title;
		}
		if (typeof props.categoryId !== 'undefined') {
			this.categoryId = props.categoryId;
		}
		if (props.typeIdVariations) {
			this.typeIdVariations = props.typeIdVariations;
		}
		if (typeof props.type !== 'undefined') {
			this.type = props.type;
		}

		this.overrideFunction = props.overrideFunction || null;
		this.overrideFormat = props.overrideFormat || null;

		if (typeof props.offset !== 'undefined') {
			this.offset = props.offset;
		}
		if (typeof props.timeUnit !== 'undefined') {
			this.timeUnit = props.timeUnit;
		}

		this.constValue = typeof props.constValue === 'number' ? props.constValue : null;

		if (props.metricSetup) {
			if (!this._metricSetup) {
				this._metricSetup = new MetricSetup(props.metricSetup);
			} else {
				const setup = this.metricSetup;
				if (props.metricSetup.key) {
					setup.setFromMetricSetupData(props.metricSetup);
				}
			}
		} else {
			this._metricSetup = new MetricSetup();
		}
		if (props.dateRange) {
			if (!this._customDateRange) {
				this.createCustomDateRange(props.dateRange.range);
			}
			this.customDateRangeCM.updateRange(props.dateRange.range);
		}
	}

	updateFromDateRange(dateRange: DateRange): void {
		// previous is an exception:
		// for custom absolute ranges, we need to update offset from the range day count
		// for others, just updating the multiplier / unit
		if (this.isPrevious) {
			if (dateRange.isCustom && !dateRange.isRolling) {
				const dateRangeCopy = dateRange.createCopy(),
					momentObjStart = moment(
						dateRangeCopy.rangeStartDate,
						DateRange.customDateFormat,
					),
					momentObjEnd = moment(dateRangeCopy.rangeEndDate, DateRange.customDateFormat),
					daysCount =
						Math.round(
							Math.abs(moment.duration(momentObjEnd.diff(momentObjStart)).asDays()),
						) + 1;

				const rangeStartDate = momentObjStart
						.subtract(daysCount, 'days')
						.format(DateRange.customDateFormat),
					rangeEndDate = momentObjEnd
						.subtract(daysCount, 'days')
						.format(DateRange.customDateFormat);

				dateRangeCopy.rangeStartDate = rangeStartDate;
				dateRangeCopy.rangeEndDate = rangeEndDate;

				this.customDateRangeCM.updateFromFormula(dateRangeCopy.formula);
				this.timeUnit = TimeUnit.DAY;
				this.offset = -1 * daysCount;
			} else {
				if (dateRange.rangeTimeUnit) {
					this.timeUnit = dateRange.rangeTimeUnit;
				}
				if (dateRange.rangeMultiplier) {
					this.offset = -1 * dateRange.rangeMultiplier;
				}
			}
		} else {
			// for others, force update selectionId with the dateRange given
			this.updateFromFormula(this.getFormula(), dateRange);
		}
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	destroy(): void {
		// nothing
	}

	static Category = CompareCategory; // like enum

	static customPreviousId = 'Previous';
	static customPreviousOffsetId = 'PreviousOffset';

	// these are for typeId only, for selectionId it's all customPreviousOffsetId
	static customPreviousOffsetDays = 'PreviousOffsetDays';
	static customPreviousOffsetWeeks = 'PreviousOffsetWeeks';
	static customPreviousOffsetMonths = 'PreviousOffsetMonths';
	static customPreviousOffsetQuarters = 'PreviousOffsetQuarters';
	static customPreviousOffsetYears = 'PreviousOffsetYears';

	static customCustomId = 'Custom';
	static customGoalId = 'Goal';
	static customOtherMetricId = 'OtherMetric';
	static customConstValueId = 'ConstValue';

	static predefinedCompares: DsCompare[] = [];

	static compareCategories = [
		{ id: CompareCategory.NONE, title: 'None' },
		{ id: CompareCategory.PREVIOUS, title: 'Previous' },
		// {id: CompareCategory.NEXT, title: 'Future'},
		{ id: CompareCategory.CUSTOM, title: 'Custom range' },
		{ id: CompareCategory.GOAL, title: 'Goal' },
		{ id: CompareCategory.OTHER_METRIC, title: 'Other metric' },
	];

	static timeUnitToTypeId(timeUnit: number): string {
		switch (timeUnit) {
			case TimeUnit.DAY:
				return this.customPreviousOffsetDays;
			case TimeUnit.WEEK:
				return this.customPreviousOffsetWeeks;
			case TimeUnit.MONTH:
				return this.customPreviousOffsetMonths;
			case TimeUnit.QUARTER:
				return this.customPreviousOffsetQuarters;
			case TimeUnit.YEAR:
				return this.customPreviousOffsetYears;
			default:
				return '';
		}
	}

	static typeIdToTimeUnit(typeId: string): number {
		switch (typeId) {
			case this.customPreviousOffsetDays:
				return TimeUnit.DAY;
			case this.customPreviousOffsetWeeks:
				return TimeUnit.WEEK;
			case this.customPreviousOffsetMonths:
				return TimeUnit.MONTH;
			case this.customPreviousOffsetQuarters:
				return TimeUnit.QUARTER;
			case this.customPreviousOffsetYears:
				return TimeUnit.YEAR;
			default:
				return 0;
		}
	}

	static getModelPropsFromFormula(
		formula: DSSetup.Compare | null,
		dateRange?: DateRange,
	): DsCompareProps {
		const props: DsCompareProps = {};

		let typeId = DsCompare.customNoneId,
			selectionId = DsCompare.customNoneId;

		if (formula) {
			props.type = formula.type;

			if (formula.id) {
				props.id = formula.id;
			}

			if (formula.overrides?.format) {
				props.overrideFormat = formula.overrides.format;
			}

			if (formula.overrides?.function) {
				props.overrideFunction = formula.overrides?.function;
			}

			if (formula.type === CompareType.OTHER_METRIC) {
				typeId = DsCompare.customOtherMetricId;
				selectionId = DsCompare.customOtherMetricId;
				if (formula.metricSetup) {
					props.metricSetup = copyObject(formula.metricSetup);
				}
			} else if (formula.type === CompareType.GOAL) {
				typeId = DsCompare.customGoalId;
				selectionId = DsCompare.customGoalId;
				if (formula.metricSetup) {
					props.metricSetup = copyObject(formula.metricSetup);
				}
			} else if (formula.type === CompareType.RANGE) {
				const constValue = formula.config?.constValue,
					formulaRange = formula.dateRange?.range,
					formulaOffset = formula.dateRange?.offset;

				if (typeof constValue === 'number') {
					selectionId = typeId = DsCompare.customConstValueId;
					props.constValue = constValue;
				} else if (formulaRange) {
					typeId = DsCompare.customCustomId;
					selectionId = DsCompare.customCustomId;

					let range;
					if (formulaRange.startDate) {
						// custom, all props present
						range = formulaRange;
					} else {
						// rolling, only diff from master range sent
						range = Object.assign({}, dateRange?.rangeFormula, formulaRange);
					}

					props.dateRange = {
						range,
					};
				} else if (formulaOffset) {
					props.offset = formulaOffset.offset;
					props.timeUnit = formulaOffset.timeUnit;

					// need for comparing previous, which is always expressed in "offset"
					// for absolute custom range or "normal" range alike
					// for absolute ranges, we need to count the days between start and end date
					// for others, it's the range multiplier and timeUnit that counts
					let daysCount, rangeTimeUnit;

					if (dateRange) {
						if (dateRange.isCustom && !dateRange.isRolling) {
							const momentObjStart = moment(
									dateRange.rangeStartDate,
									DateRange.customDateFormat,
								),
								momentObjEnd = moment(
									dateRange.rangeEndDate,
									DateRange.customDateFormat,
								);
							daysCount =
								Math.abs(
									moment.duration(momentObjEnd.diff(momentObjStart)).asDays(),
								) + 1;
							rangeTimeUnit = TimeUnit.DAY;
						} else {
							daysCount = dateRange.rangeMultiplier;
							rangeTimeUnit = dateRange.rangeTimeUnit;
						}
					}

					if (
						daysCount &&
						formulaOffset.timeUnit === rangeTimeUnit &&
						formulaOffset.offset === -1 * daysCount
					) {
						selectionId = typeId = DsCompare.customPreviousId;
					} else {
						selectionId = DsCompare.customPreviousOffsetId;
						switch (formulaOffset.timeUnit) {
							case TimeUnit.DAY:
								typeId = DsCompare.customPreviousOffsetDays;
								break;
							case TimeUnit.WEEK:
								typeId = DsCompare.customPreviousOffsetWeeks;
								break;
							case TimeUnit.MONTH:
								typeId = DsCompare.customPreviousOffsetMonths;
								break;
							case TimeUnit.QUARTER:
								typeId = DsCompare.customPreviousOffsetQuarters;
								break;
							case TimeUnit.YEAR:
								typeId = DsCompare.customPreviousOffsetYears;
								break;
						}
					}
				} else {
					// range without actual range - invalid
					// just set something so it doesn't freeze
					typeId = DsCompare.customPreviousOffsetMonths;
					selectionId = DsCompare.customPreviousId;
				}
			}
		}

		props.typeId = typeId;
		props.selectionId = selectionId;

		// update title and category from predefined, if applicable
		if (DsCompare.predefinedCompares) {
			const predefined = DsCompare.predefinedCompares.find(
				(item) => item.selectionId === props.selectionId,
			);

			if (predefined) {
				props.title = predefined.title;
				props.categoryId = predefined.categoryId;
			}
		}

		return props;
	}

	static createFromFormula(
		formula: DSSetup.Compare | null,
		dateRange?: DateRange,
		metricDefaults?: MetricDefaults,
	): DsCompare {
		const compare = new DsCompare(undefined, metricDefaults);
		compare.updateFromFormula(formula, dateRange);
		return compare;
	}

	static createPredefinedCompares(): void {
		// in correct order
		const predefined = [
			// none
			{ id: this.customNoneId, title: 'None', categoryId: CompareCategory.NONE },

			// previous
			{
				id: this.customPreviousId,
				title: 'Previous period',
				categoryId: CompareCategory.PREVIOUS,
			},
			{
				id: this.customPreviousOffsetId,
				title: 'Same period...',
				categoryId: CompareCategory.PREVIOUS,
			},

			// {id: this.customPreviousOffsetDays, title: 'Same period ... days before', timeUnit: TimeUnit.DAY, categoryId: CompareCategory.PREVIOUS},
			// {id: this.customPreviousOffsetWeeks, title: 'Same period ... weeks before', timeUnit: TimeUnit.WEEK, categoryId: CompareCategory.PREVIOUS},
			// {id: this.customPreviousOffsetMonths, title: 'Same period ... months before', timeUnit: TimeUnit.MONTH, categoryId: CompareCategory.PREVIOUS},
			// {id: this.customPreviousOffsetQuarters, title: 'Same period ... quarters before', timeUnit: TimeUnit.QUARTER, categoryId: CompareCategory.PREVIOUS},
			// {id: this.customPreviousOffsetYears, title: 'Same period ... years before', timeUnit: TimeUnit.YEAR, categoryId: CompareCategory.PREVIOUS},

			// next - skip for now

			// custom
			{ id: this.customCustomId, title: 'Custom range', categoryId: CompareCategory.CUSTOM },

			// goal & other metric
			{ id: this.customGoalId, title: 'Goal', categoryId: CompareCategory.GOAL },
			{
				id: this.customOtherMetricId,
				title: 'Other metric',
				categoryId: CompareCategory.OTHER_METRIC,
			},
		];

		const compares: DsCompare[] = [];

		predefined.forEach((pre) => {
			let typeId = pre.id,
				finalFormula: DSSetup.Compare | null;

			const typeIdVariations: string[] = []; // needed to show dynamic limitations for different types

			if (pre.id === this.customNoneId) {
				finalFormula = null;
			} else {
				const formula: DSSetup.Compare = { type: CompareType.RANGE }; // the default
				if (pre.id === this.customGoalId) {
					formula.type = CompareType.GOAL;
					typeIdVariations.push(pre.id);
				} else if (pre.id === this.customOtherMetricId) {
					formula.type = CompareType.OTHER_METRIC;
					typeIdVariations.push(pre.id);
				} else {
					formula.type = CompareType.RANGE;
					if (pre.id === this.customCustomId) {
						const dateRange = DateRange.createFromRangeUISelectionId(
							DateRangeSelectionId.CUSTOM,
						);
						formula.dateRange = { range: dateRange.rangeFormula };
						typeIdVariations.push(pre.id);
					} else if (pre.id === this.customPreviousId) {
						formula.dateRange = {
							offset: {
								// both props will be set from date range after
								timeUnit: TimeUnit.DAY,
								offset: -1,
							},
						};
						typeIdVariations.push(pre.id);
					} else if (pre.id === this.customPreviousOffsetId) {
						typeIdVariations.push(
							this.customPreviousOffsetDays,
							this.customPreviousOffsetWeeks,
							this.customPreviousOffsetMonths,
							this.customPreviousOffsetQuarters,
							this.customPreviousOffsetYears,
						);
						formula.dateRange = {
							offset: {
								timeUnit: TimeUnit.MONTH, // some default
								offset: -2,
							},
						};
						typeId = this.customPreviousOffsetMonths;
					}
				}
				finalFormula = formula;
			}

			const compare = this.createFromFormula(finalFormula);

			compare.selectionId = pre.id;
			compare.title = pre.title;
			compare.categoryId = pre.categoryId;
			compare.typeId = typeId;
			compare.typeIdVariations = typeIdVariations;

			compares.push(compare);
		});

		this.predefinedCompares = compares;
	}

	static getLimitationsForDateRange(
		dateRange: DateRange,
		lastXLimitations: MetricLimitationRange[] = [],
		customLimitation?: MetricLimitationRange,
	): MetricLimitationCompare[] {
		// General rules:
		// - no RANGE compares allowed on AllTime range (GOAL and OTHER_METRIC only)
		// - Previous compare not allowed on SinceToDate
		// - constValue allowed always

		// PRICING LIMITATIONS: check below

		const limitations: MetricLimitationCompare[] = [];

		if (dateRange.isAllTime) {
			const exceptTypeIds = [DsCompare.customGoalId, DsCompare.customOtherMetricId];

			this.predefinedCompares.forEach((compare) => {
				compare.typeIdVariations.forEach((typeId) => {
					if (!exceptTypeIds.includes(typeId)) {
						const limitation = new MetricLimitationCompare(typeId, {
							disabled: true,
							info: { message: 'No compare with AllTime available.' },
						});
						limitations.push(limitation);
					}
				});
			});
		} else if (dateRange.isSince) {
			const limitation = new MetricLimitationCompare(DsCompare.customPreviousId, {
				disabled: true,
				info: { message: 'No previous for "Since" ranges available.' },
			});
			limitations.push(limitation);
		} else {
			this.predefinedCompares.forEach((compare) => {
				// one predefined compare can have multiple limitations ("same period X timeUnits prior")
				compare.typeIdVariations.forEach((typeId) => {
					const timeUnit = this.typeIdToTimeUnit(typeId);

					let rawData;

					if (compare.isCustom && customLimitation?.isPaidLimitation) {
						rawData = customLimitation.rawData;
					}

					if (!rawData) {
						// PRICING LIMITATIONS
						// if maxMultiplier is set on LastX, the same max must be used on compare offsets

						const rangeLimitation = lastXLimitations.find(
							(limitation) =>
								DateRange.getPredefinedRangeFormula(
									limitation.id as DateRangeTypeId,
								).timeUnit === timeUnit,
						);

						if (rangeLimitation) {
							if (
								rangeLimitation.isPaidLimitation ||
								rangeLimitation.rawData?.maxMultiplier
							) {
								rawData = rangeLimitation.rawData;
							}
						}
					}

					const limitation = new MetricLimitationCompare(typeId, rawData);
					limitations.push(limitation);
				});
			});
		}

		return limitations;
	}
}
